// Contacts: the people at a customer or supplier. The types, a `useContacts()` hook, the section a
// party card shows them in, and a picker for a document that has to name one of them.
//
// A card has one email and one phone, and that is the switchboard. Who to ring about an invoice
// and who signs off a purchase used to live in the notes, where every clerk re-discovered them.
// They are rows with a role here, so "the billing contact" is something a document can ask for.
import { del, post, put, qs, useAction, useApi } from "@keel/core";
import { useState } from "react";
import { Item, ItemList, Section } from "../Section";
import { Select } from "../Select";
import { Empty, Field, Icon, Modal, Switch } from "../ui";

export type PartyKind = "customer" | "supplier";
export type ContactRole = "general" | "billing" | "purchasing" | "technical" | "management";

export const CONTACT_ROLES: { value: ContactRole; label: string }[] = [
  { value: "general", label: "General" },
  { value: "billing", label: "Billing" },
  { value: "purchasing", label: "Purchasing" },
  { value: "technical", label: "Technical" },
  { value: "management", label: "Management" },
];

export interface PartyContact {
  id: string;
  partyKind: PartyKind;
  partyId: string;
  name: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  role: ContactRole;
  /** The one a document takes for this role. At most one per role per card. */
  isPrimary: boolean;
  notes: string | null;
  /** Retired people stay on the documents that named them and are not offered for new ones. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** What the API takes on POST/PUT. The card is named on create only; a contact never moves. */
export interface PartyContactInput {
  partyKind?: PartyKind;
  partyId?: string;
  name: string;
  jobTitle?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: ContactRole;
  isPrimary?: boolean;
  notes?: string | null;
  isActive?: boolean;
}

export const roleLabel = (role: ContactRole) => CONTACT_ROLES.find((r) => r.value === role)?.label ?? role;

/**
 * The people on one card. Nothing is fetched until the card's id is known, so a form for a new
 * party can mount this before it has been saved.
 */
export function useContacts(
  partyKind: PartyKind,
  partyId: string | null | undefined,
  opts: { includeInactive?: boolean; role?: ContactRole } = {},
) {
  return useApi<PartyContact[]>(
    partyId
      ? `/contacts${qs({ partyKind, partyId, includeInactive: opts.includeInactive, role: opts.role })}`
      : null,
  );
}

export interface ContactSelectProps {
  partyKind: PartyKind;
  partyId: string | null | undefined;
  /** The chosen contact's id. */
  value: string | null | undefined;
  onChange: (id: string, contact: PartyContact | undefined) => void;
  /** Offer only people in this role. Others stay in the list, greyed out, so nobody goes looking. */
  want?: ContactRole;
  /** Allow nobody at all, with this label. */
  emptyLabel?: string;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Picking a person at the party for a document — "attention", "ordered by". Primaries come
 * first because that is almost always the answer. Retired people are not offered, except the one
 * this document already names: an old order keeps saying who placed it.
 */
export function ContactSelect({
  partyKind,
  partyId,
  value,
  onChange,
  want,
  emptyLabel,
  disabled,
  placeholder = "Choose a contact…",
}: ContactSelectProps) {
  const all = useContacts(partyKind, partyId, { includeInactive: true }).data ?? [];
  const contacts = all.filter((c) => c.isActive || c.id === value);
  return (
    <Select
      value={value ?? ""}
      onChange={(e) =>
        onChange(
          e.target.value,
          contacts.find((c) => c.id === e.target.value),
        )
      }
      disabled={disabled || !partyId}
      placeholder={partyId ? placeholder : "Choose the party first"}
    >
      {emptyLabel && <option value="">{emptyLabel}</option>}
      {contacts.map((c) => (
        <option key={c.id} value={c.id} disabled={!!want && c.role !== want}>
          {c.name}
          {c.jobTitle ? ` · ${c.jobTitle}` : ""}
          {!c.isActive
            ? " · retired"
            : want && c.role !== want
              ? ` (${roleLabel(c.role).toLowerCase()})`
              : c.isPrimary
                ? " · primary"
                : ""}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label. */
export function ContactField({
  label,
  hint,
  ...rest
}: ContactSelectProps & { label: string; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      <ContactSelect {...rest} />
    </Field>
  );
}

export interface PartyContactsProps {
  partyKind: PartyKind;
  partyId: string;
  /** The party's own write right (`masterdata.customers.write` / `masterdata.suppliers.write`). */
  canWrite: boolean;
  /** Stable id for the section to remember whether it is open. */
  id?: string;
  defaultOpen?: boolean;
}

type Draft = Partial<PartyContact>;

/**
 * The "Contacts" section of a party card: lists, adds, edits and retires contacts in place.
 *
 * Retiring is one click and no dialog, because it is undone by one tick on the edit form; the
 * retired stay reachable behind "show retired" so a leaver can be brought back or looked up.
 */
export function PartyContacts({
  partyKind,
  partyId,
  canWrite,
  id = "party-people",
  defaultOpen,
}: PartyContactsProps) {
  // One query for both views: the active list is the default, the retired are filtered in.
  const { data } = useContacts(partyKind, partyId, { includeInactive: true });
  const [showRetired, setShowRetired] = useState(false);
  const [edit, setEdit] = useState<Draft | null>(null);
  const { run, busy } = useAction();
  const all = data ?? [];
  const active = all.filter((c) => c.isActive);
  const retired = all.length - active.length;
  const shown = showRetired ? all : active;

  const save = async () => {
    if (!edit) return;
    const body: PartyContactInput = {
      name: edit.name ?? "",
      jobTitle: edit.jobTitle ?? null,
      email: edit.email ?? null,
      phone: edit.phone ?? null,
      role: edit.role ?? "general",
      isPrimary: edit.isPrimary ?? false,
      notes: edit.notes ?? null,
      isActive: edit.isActive ?? true,
    };
    const saved = await run(
      () =>
        edit.id ? put(`/contacts/${edit.id}`, body) : post("/contacts", { ...body, partyKind, partyId }),
      edit.id ? "Contact saved" : "Contact added",
    );
    if (saved) setEdit(null);
  };

  const retire = (c: PartyContact) => run(() => del(`/contacts/${c.id}`), `${c.name} retired`);

  return (
    <Section
      title="Contacts"
      id={id}
      defaultOpen={defaultOpen}
      count={active.length || undefined}
      action={
        canWrite && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setEdit({ role: "general", isActive: true })}
          >
            <Icon name="plus" size={14} />
            Add
          </button>
        )
      }
    >
      {data && shown.length === 0 ? (
        <Empty
          title="Nobody here yet"
          icon="users"
          text={
            canWrite
              ? "Add the people you deal with, and the role each one has, so a document can name the right one."
              : "No contacts have been added to this card."
          }
        />
      ) : (
        <ItemList>
          {shown.map((c) => (
            <Item
              key={c.id}
              muted={!c.isActive}
              title={c.name}
              tags={
                <>
                  <span className="perm-tag">{roleLabel(c.role)}</span>
                  {c.isPrimary && c.isActive && <span className="perm-tag">primary</span>}
                  {!c.isActive && <span className="perm-tag">retired</span>}
                </>
              }
              actions={
                canWrite && (
                  <>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setEdit(c)}
                      title="Edit"
                      aria-label={`Edit ${c.name}`}
                    >
                      <Icon name="edit" size={14} />
                    </button>
                    {c.isActive && (
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => retire(c)}
                        disabled={busy}
                        title="Retire"
                        aria-label={`Retire ${c.name}`}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    )}
                  </>
                )
              }
            >
              {c.jobTitle && <div>{c.jobTitle}</div>}
              {c.email && (
                <div>
                  <a href={`mailto:${c.email}`}>{c.email}</a>
                </div>
              )}
              {c.phone && (
                <div>
                  <a href={`tel:${c.phone}`}>{c.phone}</a>
                </div>
              )}
            </Item>
          ))}
        </ItemList>
      )}
      {retired > 0 && (
        <p className="muted sm">
          <button type="button" className="link" onClick={() => setShowRetired((s) => !s)}>
            {showRetired ? "Hide retired" : `Show ${retired} retired`}
          </button>
        </p>
      )}
      {edit && (
        <Modal
          title={edit.id ? `Contact · ${edit.name}` : "New contact"}
          onClose={() => setEdit(null)}
          busy={busy}
          submit={edit.id ? "Save" : "Add"}
          onSubmit={save}
        >
          <div className="stack">
            <div className="form-grid">
              <Field label="Name" span>
                <input
                  value={edit.name ?? ""}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  placeholder="Dace Ozola"
                />
              </Field>
              <Field label="Job title">
                <input
                  value={edit.jobTitle ?? ""}
                  onChange={(e) => setEdit({ ...edit, jobTitle: e.target.value })}
                  placeholder="Accountant"
                />
              </Field>
              <Field label="Role" hint="What a document asks for when it needs this person">
                <Select
                  value={edit.role ?? "general"}
                  onChange={(e) => setEdit({ ...edit, role: e.target.value as ContactRole })}
                >
                  {CONTACT_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={edit.email ?? ""}
                  onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                  placeholder="dace@example.com"
                />
              </Field>
              <Field label="Phone">
                <input
                  value={edit.phone ?? ""}
                  onChange={(e) => setEdit({ ...edit, phone: e.target.value })}
                  placeholder="+371 2000 0000"
                />
              </Field>
              <Field label="Notes" span>
                <textarea
                  rows={2}
                  value={edit.notes ?? ""}
                  onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                  placeholder="Prefers email; out on Fridays"
                />
              </Field>
            </div>
            <Switch
              checked={edit.isPrimary ?? false}
              onChange={(isPrimary) => setEdit({ ...edit, isPrimary })}
              label={`The ${roleLabel(edit.role ?? "general").toLowerCase()} contact`}
              hint="The one a document takes for this role. Only one person holds it; ticking it here takes it from whoever had it."
            />
            {edit.id && (
              <Switch
                checked={edit.isActive !== false}
                onChange={(isActive) => setEdit({ ...edit, isActive })}
                label={edit.isActive !== false ? "On the card" : "Retired"}
                hint="A retired contact stays on the documents that named them and is not offered for new ones."
              />
            )}
          </div>
        </Modal>
      )}
    </Section>
  );
}
